'use client';

import clsx from 'clsx';
import type { ForwardedRef, ReactNode } from 'react';
import {
  TagGroup as RACTagGroup,
  TagList as RACTagList,
  type TagGroupProps as RACTagGroupProps,
  type TagListProps as RACTagListProps,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import { FormFieldDescription, FormFieldErrorText, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { CollectionItem, CommonProps, FieldProps, Key } from '../types.js';

export interface TagGroupProps<T extends CollectionItem = CollectionItem>
  extends
    CommonProps,
    // `TagGroup` doesn't support `validate` or `error` because it's not a form field
    Omit<FieldProps<Key[]>, 'validate' | 'error' | 'form' | 'name'>,
    Pick<
      RACTagGroupProps,
      'selectionMode' | 'defaultSelectedKeys' | 'selectedKeys' | 'disabledKeys' | 'onSelectionChange' | 'onRemove'
    >,
    Pick<RACTagListProps<T>, 'children' | 'items'> {
  /** Whether the tag group is required. */
  isRequired?: boolean;
  /** The error message to display. */
  error?: string;
  /** The size of the tags. */
  size?: 'medium' | 'large';
  /** Provides content to display when there are no tags in the group. */
  renderEmptyState?: () => ReactNode;
}

function TagGroup<T extends CollectionItem>(
  {
    label,
    description,
    error,
    children,
    isRequired,
    items,
    renderEmptyState,
    size = 'medium',
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: TagGroupProps<T>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  if (props.selectionMode != null && props.selectionMode !== 'none' && props.onRemove != null) {
    console.error('`onRemove` is not supported when `selectionMode` is set to anything other than `"none"`');
  }

  return (
    <RACTagGroup
      {...props}
      ref={ref}
      className={clsx('cim-tag-group', UNSAFE_className)}
      style={UNSAFE_style}
      data-size={size}
    >
      <FormFieldLabel isRequired={isRequired}>{label}</FormFieldLabel>
      <RACTagList className="cim-tag-group-list" items={items} renderEmptyState={renderEmptyState}>
        {children}
      </RACTagList>
      {error && <FormFieldErrorText>{error}</FormFieldErrorText>}
      <FormFieldDescription>{description}</FormFieldDescription>
    </RACTagGroup>
  );
}

/**
 * Displays a group of tags. Allows for selecting or removing tags.
 *
 * See [tag group usage guidelines](https://ui.cimpress.io/components/tag-group/).
 */
const _TagGroup = withStyleProps(forwardRef(TagGroup), 'TagGroup');

export { _TagGroup as TagGroup };
